import { addDaysIsoDate, todayIsoDate } from '@/lib/formatters';
import type {
  AdminLibrary,
  CompanyContractKind,
  CompanyContractStatus,
  CompanyCredentialKind,
} from '@/types/database';

export const ADMIN_FILES_BUCKET = 'admin-files';
export const MAX_ADMIN_FILE_BYTES = 50 * 1024 * 1024;

export type AdminFileKind = 'pdf' | 'word' | 'image' | 'font' | 'design' | 'slides' | 'file';

export interface AdminFileType {
  ext: string;
  /**
   * Content type we store the object with. Browsers leave `File.type`
   * blank for most font and design formats, and Supabase would then fall
   * back to text/plain — which makes a signed-URL open render bytes as
   * text. Pinning the type by extension keeps downloads predictable.
   */
  mime: string;
  kind: AdminFileKind;
}

interface AdminFileGroup {
  label: string;
  types: readonly AdminFileType[];
}

const DOCUMENT_TYPES: AdminFileGroup = {
  label: 'PDF or Word',
  types: [
    { ext: 'pdf', mime: 'application/pdf', kind: 'pdf' },
    { ext: 'doc', mime: 'application/msword', kind: 'word' },
    {
      ext: 'docx',
      mime: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      kind: 'word',
    },
  ],
};

const IMAGE_TYPES: AdminFileGroup = {
  label: 'image',
  types: [
    { ext: 'png', mime: 'image/png', kind: 'image' },
    { ext: 'jpg', mime: 'image/jpeg', kind: 'image' },
    { ext: 'jpeg', mime: 'image/jpeg', kind: 'image' },
    { ext: 'webp', mime: 'image/webp', kind: 'image' },
    { ext: 'gif', mime: 'image/gif', kind: 'image' },
    { ext: 'svg', mime: 'image/svg+xml', kind: 'image' },
  ],
};

const FONT_TYPES: AdminFileGroup = {
  label: 'font (TTF, OTF, WOFF)',
  types: [
    { ext: 'ttf', mime: 'font/ttf', kind: 'font' },
    { ext: 'otf', mime: 'font/otf', kind: 'font' },
    { ext: 'woff', mime: 'font/woff', kind: 'font' },
    { ext: 'woff2', mime: 'font/woff2', kind: 'font' },
  ],
};

const DESIGN_TYPES: AdminFileGroup = {
  label: 'design source (AI, EPS, PSD, INDD, Sketch, Figma)',
  types: [
    { ext: 'ai', mime: 'application/postscript', kind: 'design' },
    { ext: 'eps', mime: 'application/postscript', kind: 'design' },
    { ext: 'psd', mime: 'image/vnd.adobe.photoshop', kind: 'design' },
    { ext: 'indd', mime: 'application/x-indesign', kind: 'design' },
    { ext: 'sketch', mime: 'application/octet-stream', kind: 'design' },
    { ext: 'fig', mime: 'application/octet-stream', kind: 'design' },
  ],
};

const PRESENTATION_TYPES: AdminFileGroup = {
  label: 'presentation (PPT, PPTX, Keynote)',
  types: [
    { ext: 'ppt', mime: 'application/vnd.ms-powerpoint', kind: 'slides' },
    {
      ext: 'pptx',
      mime: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      kind: 'slides',
    },
    { ext: 'key', mime: 'application/x-iwork-keynote-sffkey', kind: 'slides' },
  ],
};

/**
 * What each library accepts. This is an explicit allowlist on purpose:
 * anything executable, scriptable, or archive-shaped (exe, html, js, zip)
 * stays out so the shared drive can't become a delivery vector. The
 * `admin_files_extension` check constraint mirrors this list — update
 * both together.
 */
export const ADMIN_LIBRARY_FILE_GROUPS: Record<AdminLibrary, readonly AdminFileGroup[]> = {
  templates: [DOCUMENT_TYPES],
  brand: [DOCUMENT_TYPES, IMAGE_TYPES, FONT_TYPES, DESIGN_TYPES, PRESENTATION_TYPES],
};

const ALL_FILE_TYPES: readonly AdminFileType[] = [
  DOCUMENT_TYPES,
  IMAGE_TYPES,
  FONT_TYPES,
  DESIGN_TYPES,
  PRESENTATION_TYPES,
].flatMap((group) => group.types);

const FILE_TYPE_BY_EXT = new Map(ALL_FILE_TYPES.map((type) => [type.ext, type]));

export function adminLibraryFileTypes(library: AdminLibrary): readonly AdminFileType[] {
  return ADMIN_LIBRARY_FILE_GROUPS[library].flatMap((group) => group.types);
}

/** Value for an `<input type="file" accept>` covering the library's allowlist. */
export function adminLibraryAccept(library: AdminLibrary): string {
  const types = adminLibraryFileTypes(library);
  const exts = types.map((type) => `.${type.ext}`);
  const mimes = [...new Set(types.map((type) => type.mime))].filter(
    (mime) => mime !== 'application/octet-stream',
  );
  return [...exts, ...mimes].join(',');
}

/** Human-readable summary, e.g. "PDF or Word, image, or font (TTF, OTF, WOFF) files". */
export function adminLibraryAllowedSummary(library: AdminLibrary): string {
  const labels = ADMIN_LIBRARY_FILE_GROUPS[library].map((group) => group.label);
  if (labels.length === 1) return `${labels[0]} files`;
  return `${labels.slice(0, -1).join(', ')}, or ${labels[labels.length - 1]} files`;
}

export const TEMPLATE_ACCEPT = adminLibraryAccept('templates');
export const BRAND_ACCEPT = adminLibraryAccept('brand');

export const TEMPLATE_STARTER_FOLDERS = [
  'HR forms',
  'Vendor packets',
  'Customer onboarding',
  'Shipping & logistics',
  'Finance forms',
  'Facilities',
] as const;

export const BRAND_STARTER_FOLDERS = [
  'Logos',
  'Brand guidelines',
  'Letterhead & stationery',
] as const;

export const CREDENTIAL_KINDS: CompanyCredentialKind[] = [
  'business_license',
  'permit',
  'insurance',
  'registration',
  'tax',
  'other',
];

export const CREDENTIAL_KIND_LABELS: Record<CompanyCredentialKind, string> = {
  business_license: 'Business license',
  permit: 'Permit',
  insurance: 'Insurance',
  registration: 'Registration',
  tax: 'Tax',
  other: 'Other',
};

export const CONTRACT_KINDS: CompanyContractKind[] = [
  'vendor',
  'customer',
  'lease',
  'nda',
  'insurance',
  'other',
];

export const CONTRACT_KIND_LABELS: Record<CompanyContractKind, string> = {
  vendor: 'Vendor',
  customer: 'Customer',
  lease: 'Lease',
  nda: 'NDA',
  insurance: 'Insurance',
  other: 'Other',
};

export const CONTRACT_STATUSES: CompanyContractStatus[] = [
  'draft',
  'active',
  'expired',
  'terminated',
];

export const CONTRACT_STATUS_LABELS: Record<CompanyContractStatus, string> = {
  draft: 'Draft',
  active: 'Active',
  expired: 'Expired',
  terminated: 'Terminated',
};

export const CONTACT_ROLE_SUGGESTIONS = [
  'Attorney',
  'Accountant',
  'Insurance broker',
  'Banker',
  'Landlord',
  'Benefits administrator',
  'IT support',
  'Freight broker',
];

export type ExpiryState = 'none' | 'current' | 'expiring' | 'expired';

export function fileExtension(name: string): string {
  const trimmed = name.trim();
  const dot = trimmed.lastIndexOf('.');
  if (dot <= 0 || dot === trimmed.length - 1) return '';
  return trimmed.slice(dot + 1).toLowerCase();
}

export function isAllowedAdminFile(library: AdminLibrary, name: string): boolean {
  const ext = fileExtension(name);
  return adminLibraryFileTypes(library).some((type) => type.ext === ext);
}

export function adminFileKind(name: string): AdminFileKind {
  return FILE_TYPE_BY_EXT.get(fileExtension(name))?.kind ?? 'file';
}

/**
 * Content type to store an upload under. The extension has already been
 * checked against the allowlist, so its mapped type is trusted over the
 * browser's guess; anything unmapped is stored as an opaque binary so it
 * is downloaded rather than rendered.
 */
export function adminUploadContentType(file: File): string {
  const known = FILE_TYPE_BY_EXT.get(fileExtension(file.name));
  if (known) return known.mime;
  return file.type || 'application/octet-stream';
}

/** Browsers can show these inline; everything else should download. */
export function adminFileOpensInline(name: string): boolean {
  const kind = adminFileKind(name);
  return kind === 'pdf' || kind === 'image';
}

export const ADMIN_FILE_KIND_LABELS: Record<AdminFileKind, string> = {
  pdf: 'PDF',
  word: 'Word',
  image: 'Image',
  font: 'Font',
  design: 'Design',
  slides: 'Slides',
  file: 'File',
};

/** Keep a rename inside the library's allowed extensions. */
export function preserveAllowedExtension(
  library: AdminLibrary,
  nextName: string,
  originalName: string,
): string {
  const trimmed = nextName.trim();
  if (isAllowedAdminFile(library, trimmed)) return trimmed;
  const ext = fileExtension(originalName);
  const stem = trimmed.replace(/\.[^.]+$/, '').trim() || trimmed;
  return ext ? `${stem}.${ext}` : stem;
}

export function safeStorageName(filename: string): string {
  const base = filename.split(/[/\\]/).pop() ?? 'file';
  const cleaned = base.replace(/[^a-zA-Z0-9._-]+/g, '_').replace(/^_+|_+$/g, '');
  return (cleaned || 'file').slice(0, 160);
}

export function blankToNull(value: string | null | undefined): string | null {
  if (value == null) return null;
  const trimmed = value.trim();
  return trimmed.length ? trimmed : null;
}

export function formatBytes(bytes: number | null | undefined): string {
  if (bytes == null) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) {
    const kb = bytes / 1024;
    return `${kb >= 10 ? Math.round(kb) : kb.toFixed(1)} KB`;
  }
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Calendar-date expiry. "Expiring" is the next 45 days — long enough
 * to renew a license or request a fresh certificate of insurance.
 */
export function expiryState(expiresOn: string | null | undefined): ExpiryState {
  if (!expiresOn) return 'none';
  const day = expiresOn.slice(0, 10);
  const today = todayIsoDate();
  if (day < today) return 'expired';
  if (day <= addDaysIsoDate(today, 45)) return 'expiring';
  return 'current';
}

export function friendlyAdminError(err: unknown, fallback: string): string {
  const msg = err instanceof Error ? err.message : '';
  if (/admin_folders_sibling_name_uniq|duplicate key value/i.test(msg)) {
    return 'A folder with that name is already here.';
  }
  if (/cycle/i.test(msg)) return 'A folder can’t be moved into itself.';
  if (/25 levels/i.test(msg)) return 'That folder is nested too deeply.';
  if (/extension|file_ext/i.test(msg)) return 'That file type isn’t allowed here.';
  if (/50 MB|payload too large|file size/i.test(msg)) return 'Files must be 50 MB or smaller.';
  return msg || fallback;
}

export function formatStreetAddress(parts: {
  line1: string | null;
  line2: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
}): string {
  const cityLine = [parts.city, parts.state].filter(Boolean).join(', ');
  const locality = [cityLine, parts.zip].filter(Boolean).join(' ');
  return [parts.line1, parts.line2, locality].filter((part) => !!part && part.trim()).join('\n');
}
